import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { baseAPI } from '../../config';
import UpdateDebtor from '../UpdateDebtorModal';
import CallModal from '../CallModal';
import userStore from '../../store/userStore';
import { COUNTRY_PREFIXES, DEFAULT_COUNTRY_PREFIX } from '@claimup/shared/constants/country-prefixes';
import { canCommunicate } from '@claimup/shared/helpers/permissions';
import { isPartnerRole } from '@claimup/shared/constants/roles';
import { EXPEDIENT_TYPES } from '@claimup/shared/constants/expedient';
import { AR_SMS_MAX_LENGTH } from '@claimup/shared/constants/ar-arranque';
import { Link } from 'react-router-dom';
import { VentanaCerrada } from '../../pages/ManagementListPage/components/WhatsAppChat/WhatsAppChat';

/** El cuerpo se escribe en texto y la ruta lo manda como HTML: sin esto se pierden los saltos de línea. */
const escapeHtml = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const textoAHtml = texto =>
  texto
    .trim()
    .split(/\n{2,}/)
    .map(p => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');

const Debtors = ({ idExpedient }) => {
  const [deudores, setDeudores] = useState([]);
  const [expedientType, setExpedientType] = useState(null);
  const [crearDeudores, setCrearDeudores] = useState(false);
  const [isWhatsappModalOpen, setIsWhatsappModalOpen] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [selectedDebtorId, setSelectedDebtorId] = useState(null);
  const [selectedPhoneId, setSelectedPhoneId] = useState('');
  // Números WhatsApp seleccionables para este expediente (scopeados por rol en
  // el backend): { phone_id, owned }. El envío real valida server-side.
  const [waNumbers, setWaNumbers] = useState([]);
  const [nuevoDeudor, setNuevoDeudor] = useState({
    nombre: '',
    domicilio: '',
    ciudad: '',
    cp: '',
    telefono: '',
    country_prefix: DEFAULT_COUNTRY_PREFIX,
    mail: '',
  });
  const { user } = userStore();

  // Prepare country prefix options for react-select
  const countryPrefixOptions = COUNTRY_PREFIXES.map(prefix => ({
    value: prefix.code,
    label: `${prefix.flag} +${prefix.code} ${prefix.country}`,
  }));
  const userRole = user?.role;
  // Acciones de comunicación con el deudor: admin siempre; gestor/company en AR.
  const isAr = expedientType === EXPEDIENT_TYPES.ACCOUNT_RECEIVABLE;
  // Lo que su empresa le deja hacer al gestor. Solo esconde botones: quien decide
  // es el servidor. `null` = aún no se sabe → se enseña.
  const [gestorPermisos, setGestorPermisos] = useState(null);
  const puede = cap => userRole !== 'gestor' || gestorPermisos?.[cap] !== false;
  const canComm = canCommunicate(userRole, expedientType) && puede('communicate');
  // En AR llama el gestor con SU número, desde la sesión de llamadas
  // (docs/contactar-deudor-ar-plan.md). Fuera de AR, como siempre.
  const llamaDesdeSesion = isAr && userRole === 'gestor' && puede('call');
  const llamaPorAircall = !isAr && puede('call');

  // La ventana de 24 h de Meta (WhatsApp de AR) y las plantillas de reenganche.
  const [replyWindow, setReplyWindow] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [enviandoPlantilla, setEnviandoPlantilla] = useState(false);
  const ventanaCerrada = Boolean(isAr && replyWindow && replyWindow.applies && !replyWindow.free_form_allowed);

  // Email y SMS escritos a mano en AR.
  const [correo, setCorreo] = useState(null); // { debtorId, asunto, cuerpo }
  const [sms, setSms] = useState(null); // { debtorId, texto }
  const [enviando, setEnviando] = useState(false);
  const cabeceras = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const fetchExpediente = async () => {
      try {
        const response = await axios.get(`${baseAPI}/expedients/${idExpedient}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setDeudores(response.data.deudores_id || []);
        setExpedientType(response.data.type || null);
      } catch (error) {
        console.error('Error fetching expediente:', error);
      }
    };

    const fetchWaNumbers = async () => {
      try {
        const { data } = await axios.get(
          `${baseAPI}/expedients/${idExpedient}/sender-numbers?channel=whatsapp`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        setWaNumbers(data?.data?.numbers || []);
        setReplyWindow(data?.data?.reply_window || null);
      } catch (error) {
        setWaNumbers([]);
      }
    };

    if (userRole === 'gestor') {
      axios
        .get(`${baseAPI}/team-permissions`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then(({ data }) => setGestorPermisos(data?.data?.gestor || null))
        .catch(() => setGestorPermisos(null));
    }

    fetchExpediente();
    // El despacho no se comunica con el deudor: no pide los números de envío.
    if (!isPartnerRole(userRole)) fetchWaNumbers();
  }, [idExpedient, userRole]);

  // Teléfonos WhatsApp seleccionables: los resuelve el backend según el rol y el
  // tenant del expediente (sender-numbers), ya con el flag `owned`. Los de la
  // empresa primero. NO se cruza con el store de settings (admin-only), para que
  // el gestor vea los números de su company.
  const scopedWaPhones = [...waNumbers].sort((a, b) => Number(b.owned) - Number(a.owned));

  // Inicializa el teléfono seleccionado (primer número permitido) cuando llegan.
  useEffect(() => {
    if (scopedWaPhones.length > 0 && !selectedPhoneId) {
      setSelectedPhoneId(scopedWaPhones[0].phone_id);
    }
  }, [scopedWaPhones, selectedPhoneId]);

  const handleNuevoDeudorChange = e => {
    const { name, value } = e.target;
    setNuevoDeudor({ ...nuevoDeudor, [name]: value });
  };

  // Handle country prefix change from react-select
  const handlePrefixChange = selectedOption => {
    setNuevoDeudor({ ...nuevoDeudor, country_prefix: selectedOption.value });
  };

  const handleSubmitNuevoDeudor = async e => {
    e.preventDefault();
    try {
      await axios.put(
        `${baseAPI}/expedients/adddebtor`,
        {
          ...nuevoDeudor,
          expedient_id: idExpedient,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('Deudor añadido correctamente');
      window.location.reload();
    } catch (error) {
      console.error('Error añadiendo deudor:', error);
      toast.error('Error al añadir el deudor');
    }
  };

  const openWhatsappModal = debtorId => {
    setSelectedDebtorId(debtorId);
    // Por defecto, el primer número del pool permitido (empresa primero).
    const current = scopedWaPhones.find(p => p.phone_id === selectedPhoneId);
    if (!current && scopedWaPhones.length > 0) {
      setSelectedPhoneId(scopedWaPhones[0].phone_id);
    }
    setIsWhatsappModalOpen(true);
  };

  const handleSendWhatsapp = async e => {
    e.preventDefault();

    // Validate phone selection (pool scopeado por el backend)
    if (scopedWaPhones.length === 0) {
      toast.error('No hay teléfonos disponibles para enviar WhatsApp');
      return;
    }

    if (!selectedPhoneId) {
      toast.error('Debes seleccionar un teléfono para enviar el mensaje');
      return;
    }

    try {
      const selectedPhone = scopedWaPhones.find(phone => phone.phone_id === selectedPhoneId);
      if (!selectedPhone) {
        toast.error('Teléfono seleccionado no válido');
        return;
      }

      await axios.post(
        `${baseAPI}/actions/sendwhatsapp`,
        {
          debtorId: selectedDebtorId,
          expedientId: idExpedient,
          message: whatsappMessage,
          phone_id: selectedPhone.phone_id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('WhatsApp enviado correctamente');
      setWhatsappMessage('');
      setIsWhatsappModalOpen(false);
    } catch (error) {
      console.error('Error enviando el WhatsApp:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error al enviar el WhatsApp';
      toast.error(errorMessage);
    }
  };

  // Las plantillas de reenganche se piden solo cuando hacen falta.
  useEffect(() => {
    if (!isWhatsappModalOpen || !ventanaCerrada) return;
    axios
      .get(`${baseAPI}/kapso/expedients/${idExpedient}/templates`, cabeceras())
      .then(res => setTemplates(res.data?.data?.templates || []))
      .catch(() => setTemplates([]));
  }, [isWhatsappModalOpen, ventanaCerrada, idExpedient]);

  const handleSendTemplate = async key => {
    setEnviandoPlantilla(true);
    try {
      await axios.post(
        `${baseAPI}/kapso/expedients/${idExpedient}/send-template`,
        { key, debtor_id: selectedDebtorId },
        cabeceras()
      );
      toast.success('Plantilla enviada');
      setIsWhatsappModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || error.response?.data?.error || 'No se pudo enviar la plantilla');
    } finally {
      setEnviandoPlantilla(false);
    }
  };

  // En AR el correo sale por el dominio de la empresa (Resend), con asunto y texto,
  // por la misma ruta que el chat de email.
  const enviarCorreoAr = async e => {
    e.preventDefault();
    setEnviando(true);
    try {
      await axios.post(
        `${baseAPI}/expedients/${idExpedient}/emails/send`,
        { subject: correo.asunto.trim(), body: textoAHtml(correo.cuerpo) },
        cabeceras()
      );
      toast.success('Email enviado correctamente');
      setCorreo(null);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || error.response?.data?.error || 'Error al enviar el email');
    } finally {
      setEnviando(false);
    }
  };

  const enviarSmsAr = async e => {
    e.preventDefault();
    setEnviando(true);
    try {
      await axios.post(
        `${baseAPI}/actions/sendsmstodebtor`,
        { debtorId: sms.debtorId, expedientId: idExpedient, message: sms.texto.trim() },
        cabeceras()
      );
      toast.success('SMS enviado correctamente');
      setSms(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar el SMS');
    } finally {
      setEnviando(false);
    }
  };

  const handleEmailToDebtor = async debtorId => {
    if (isAr) {
      setCorreo({ debtorId, asunto: '', cuerpo: '' });
      return;
    }
    if (window.confirm('¿Seguro que quieres enviar el mail?')) {
      try {
        await axios.post(
          `${baseAPI}/actions/sendEmailToDebtor`,
          {
            debtorId,
            expedientId: idExpedient,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        toast.success('Email enviado correctamente');
      } catch (error) {
        console.error('Error enviando el email:', error);
        toast.error('Error al enviar el email');
      }
    }
  };

  const handleSmsToDebtor = async debtorId => {
    if (isAr) {
      setSms({ debtorId, texto: '' });
      return;
    }
    if (window.confirm('¿Seguro que quieres enviar un SMS?')) {
      try {
        await axios.post(
          `${baseAPI}/actions/sendsmstodebtor`,
          {
            debtorId,
            expedientId: idExpedient,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        toast.success('SMS enviado correctamente');
      } catch (error) {
        console.error('Error enviando el SMS:', error);
        toast.error(error.response?.data?.error || 'Error al enviar el SMS 😞');
      }
    }
  };


  return (
    <div className='col-span-full xl:col-span-12 bg-white shadow-lg rounded-lg border border-gray-200'>
      <header className='px-5 py-4 border-b border-gray-100'>
        <h2 className='font-semibold text-gray-800'>Deudores</h2>
      </header>

      <div className='p-5'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
          {deudores.map(deudor => (
            <div
              key={deudor._id}
              className='bg-gray-50 border border-gray-200 rounded-lg shadow-sm'
            >
              <div className='p-4'>
                <div className='flex items-center justify-between mb-3'>
                  <h3 className='text-sm font-semibold text-gray-800 truncate'>{deudor.debtorname}</h3>
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
                    Activo
                  </span>
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center text-sm text-gray-600'>
                    <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                      />
                    </svg>
                    +{deudor.country_prefix || DEFAULT_COUNTRY_PREFIX} {deudor.debtorphone}
                  </div>
                  <div className='flex items-center text-sm text-gray-600'>
                    <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                      />
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                      />
                    </svg>
                    {deudor.debtoradress}, {deudor.debtorcity}, {deudor.debtorzip}
                  </div>
                </div>
              </div>

              {canComm && (
                <div className='border-t border-gray-200 p-4 bg-gray-50'>
                  <div className='grid grid-cols-2 gap-2'>
                    <button
                      onClick={() => handleEmailToDebtor(deudor._id)}
                      className='inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    >
                      <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                        />
                      </svg>
                      Email
                    </button>
                    <button
                      onClick={() => handleSmsToDebtor(deudor._id)}
                      className='inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    >
                      <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          d='M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z'
                        />
                      </svg>
                      SMS
                    </button>
                    {llamaPorAircall && (
                      <CallModal
                        expedientId={idExpedient}
                        debtorId={deudor._id}
                        buttonClassName='inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                      />
                    )}
                    {/* En AR, a la sesión de llamadas con el deudor ya buscado: la
                        voz va por el navegador desde allí, con el número del gestor. */}
                    {llamaDesdeSesion && (
                      <Link
                        to={`/call-session?buscar=${encodeURIComponent(deudor.debtorname || '')}`}
                        className='inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100'
                      >
                        Llamar
                      </Link>
                    )}
                    <UpdateDebtor idDebtor={deudor._id} />
                    <button
                      onClick={() => openWhatsappModal(deudor._id)}
                      className='inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    >
                      <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
                        />
                      </svg>
                      WhatsApp
                    </button>
                  </div>
                  {isAr && userRole !== 'gestor' && (
                    <p className='mt-2 text-xs text-gray-500'>
                      En AR llama el gestor, desde su sesión de llamadas y con su número.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Email en AR: asunto y texto, por el dominio de la empresa */}
        {correo && (
          <div className='fixed z-10 inset-0 overflow-y-auto'>
            <div className='flex items-center justify-center min-h-screen px-4'>
              <div className='fixed inset-0 bg-gray-500 bg-opacity-75'></div>
              <form
                onSubmit={enviarCorreoAr}
                aria-label='Email al deudor'
                className='relative bg-white rounded-lg shadow-xl sm:max-w-lg w-full p-6 space-y-3'
              >
                <h3 className='text-lg font-semibold text-gray-800'>Email al deudor</h3>
                <p className='text-xs text-gray-500'>Sale por el correo de la empresa.</p>
                <input
                  aria-label='Asunto'
                  placeholder='Asunto'
                  value={correo.asunto}
                  onChange={e => setCorreo(c => ({ ...c, asunto: e.target.value }))}
                  className='block w-full border border-gray-300 rounded-lg p-2 text-sm'
                  required
                />
                <textarea
                  aria-label='Texto del email'
                  rows='6'
                  value={correo.cuerpo}
                  onChange={e => setCorreo(c => ({ ...c, cuerpo: e.target.value }))}
                  className='block w-full border border-gray-300 rounded-lg p-2 text-sm'
                  required
                />
                <div className='flex justify-end gap-2'>
                  <button type='button' onClick={() => setCorreo(null)} className='px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg'>
                    Cancelar
                  </button>
                  <button
                    type='submit'
                    disabled={enviando || !correo.asunto.trim() || !correo.cuerpo.trim()}
                    className='px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50'
                  >
                    Enviar email
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* SMS en AR: el texto lo escribe quien lo manda, hasta 160 caracteres */}
        {sms && (
          <div className='fixed z-10 inset-0 overflow-y-auto'>
            <div className='flex items-center justify-center min-h-screen px-4'>
              <div className='fixed inset-0 bg-gray-500 bg-opacity-75'></div>
              <form
                onSubmit={enviarSmsAr}
                aria-label='SMS al deudor'
                className='relative bg-white rounded-lg shadow-xl sm:max-w-lg w-full p-6 space-y-3'
              >
                <h3 className='text-lg font-semibold text-gray-800'>SMS al deudor</h3>
                <p className='text-xs text-gray-500'>
                  Solo sale si la empresa tiene el SMS activado, y como mucho 2 por expediente sin respuesta del deudor.
                </p>
                <textarea
                  aria-label='Texto del SMS'
                  rows='3'
                  maxLength={AR_SMS_MAX_LENGTH}
                  value={sms.texto}
                  onChange={e => setSms(s => ({ ...s, texto: e.target.value }))}
                  className='block w-full border border-gray-300 rounded-lg p-2 text-sm'
                  required
                />
                <p className='text-xs text-gray-400 text-right'>
                  {sms.texto.length}/{AR_SMS_MAX_LENGTH}
                </p>
                <div className='flex justify-end gap-2'>
                  <button type='button' onClick={() => setSms(null)} className='px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg'>
                    Cancelar
                  </button>
                  <button
                    type='submit'
                    disabled={enviando || !sms.texto.trim()}
                    className='px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50'
                  >
                    Enviar SMS
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de WhatsApp */}
        {isWhatsappModalOpen && (
          <div className='fixed z-10 inset-0 overflow-y-auto'>
            <div className='flex items-center justify-center min-h-screen px-4'>
              <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity'></div>
              <div className='bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full'>
                <div className='bg-white p-6'>
                  <h3 className='text-lg font-semibold text-gray-800 mb-4'>Enviar WhatsApp Personalizado</h3>
                  {/* Fuera de la ventana de 24 h, Meta solo deja una plantilla aprobada:
                      el mismo bloque que el chat de WhatsApp. */}
                  {ventanaCerrada ? (
                    <>
                      <VentanaCerrada
                        replyWindow={replyWindow}
                        templates={templates}
                        onEnviar={handleSendTemplate}
                        enviando={enviandoPlantilla}
                        senderReady={scopedWaPhones.length > 0}
                        esAdmin={userRole === 'admin'}
                      />
                      <div className='flex justify-end mt-3'>
                        <button
                          type='button'
                          onClick={() => setIsWhatsappModalOpen(false)}
                          className='px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg'
                        >
                          Cerrar
                        </button>
                      </div>
                    </>
                  ) : (
                  <form onSubmit={handleSendWhatsapp}>
                    <div className='mb-4'>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>Teléfono:</label>
                      {scopedWaPhones.length === 0 ? (
                        <p className='text-sm text-red-600 mb-2'>No hay teléfonos disponibles</p>
                      ) : (
                        <select
                          value={selectedPhoneId}
                          onChange={e => setSelectedPhoneId(e.target.value)}
                          className='block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white'
                          required
                        >
                          {scopedWaPhones.map(phone => (
                            <option key={phone.phone_id} value={phone.phone_id}>
                              {phone.owned ? '★ ' : ''}{phone.name} - {phone.phone_number}{phone.owned ? ' (empresa)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className='mb-4'>
                      <label className='block text-sm font-medium text-gray-700'>Mensaje:</label>
                      <textarea
                        className='mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500'
                        rows='4'
                        value={whatsappMessage}
                        onChange={e => setWhatsappMessage(e.target.value)}
                        required
                      ></textarea>
                    </div>
                    <div className='flex justify-end space-x-2'>
                      <button
                        type='button'
                        className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        onClick={() => setIsWhatsappModalOpen(false)}
                      >
                        Cancelar
                      </button>
                      <button
                        type='submit'
                        className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                      >
                        Enviar WhatsApp
                      </button>
                    </div>
                  </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Formulario de nuevo deudor */}
        {userRole === 'admin' &&
          (!crearDeudores ? (
            <div className='mt-6'>
              <button
                onClick={() => setCrearDeudores(true)}
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 4v16m8-8H4' />
                </svg>
                Añadir deudor
              </button>
            </div>
          ) : (
            <div className='mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6'>
              <h3 className='text-lg font-semibold text-gray-800 mb-4'>Añadir un nuevo Deudor</h3>
              <form onSubmit={handleSubmitNuevoDeudor}>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {['nombre', 'domicilio', 'ciudad', 'cp', 'mail'].map(campo => (
                    <div key={campo}>
                      <label className='block text-sm font-medium text-gray-700 capitalize'>{campo}</label>
                      <input
                        type='text'
                        name={campo}
                        value={nuevoDeudor[campo]}
                        onChange={handleNuevoDeudorChange}
                        className='mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white'
                        required
                      />
                    </div>
                  ))}

                  {/* Phone field with country prefix selector */}
                  <div className='sm:col-span-2'>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Teléfono</label>
                    <div className='flex gap-2'>
                      <div className='w-48'>
                        <Select
                          options={countryPrefixOptions}
                          value={countryPrefixOptions.find(option => option.value === nuevoDeudor.country_prefix)}
                          onChange={handlePrefixChange}
                          placeholder='Prefijo'
                          isSearchable={true}
                          className='react-select-container'
                          classNamePrefix='react-select'
                          styles={{
                            control: base => ({
                              ...base,
                              borderRadius: '0.5rem',
                              minHeight: '38px',
                            }),
                          }}
                        />
                      </div>
                      <input
                        type='text'
                        name='telefono'
                        value={nuevoDeudor.telefono}
                        onChange={handleNuevoDeudorChange}
                        className='flex-1 border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white'
                        placeholder='Número de teléfono'
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className='mt-6 flex justify-end space-x-3'>
                  <button
                    type='button'
                    onClick={() => setCrearDeudores(false)}
                    className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  >
                    Cancelar
                  </button>
                  <button
                    type='submit'
                    className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  >
                    Guardar deudor
                  </button>
                </div>
              </form>
            </div>
          ))}
      </div>
    </div>
  );
};

export default Debtors;
